'use client'

import React, { Suspense } from 'react'

import { useQuery } from '@tanstack/react-query'

import {
  useRecipientAnalyticsQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import { HistogramChart } from '../../charts/HistogramChart'
import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { ChartUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

/**
 * RecipientDetail.ContractSizes
 *
 * Histogram showing contract size distribution in logarithmic buckets.
 * Reveals typical award sizes and distribution patterns.
 */
export function ContractSizesSection() {
  const { recipientId } = useRecipientDetail()

  return (
    <SectionCard
      id="contract-sizes"
      title="Contract Size Distribution"
      animationDelay={350}
    >
      <QueryErrorBoundary
        title="Failed to load contract sizes"
        serviceUnavailableFallback={<ChartUnavailable height={220} />}
      >
        <Suspense fallback={<SectionSkeleton height={220} bars={0} />}>
          <ContractSizesContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function ContractSizesContent({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)

  if (dashboard.contractSizes === null) {
    return <DeferredContractSizes id={id} fiscalYear={fiscalYear} />
  }

  return (
    <HistogramChart
      data={dashboard.contractSizes}
      height={180}
      accentColor="purple"
    />
  )
}

function DeferredContractSizes({
  id,
  fiscalYear,
}: {
  id: string
  fiscalYear?: number
}) {
  const queries = useRecipientAnalyticsQueries()
  const { data, isLoading } = useQuery(queries.contractSizes(id, fiscalYear))

  if (isLoading) return <SectionSkeleton height={220} bars={0} />
  if (!data) return null

  return <HistogramChart data={data} height={180} accentColor="purple" />
}
